import Phaser from 'phaser'


type MenuOption = {
  label: string
  key: string
  enabled: boolean
}

export class MenuScene extends Phaser.Scene {
  private options: MenuOption[] = [
    { label: 'Jugar', key: 'play', enabled: true },
    { label: 'Continuar', key: 'continue', enabled: false },
    { label: 'Opciones', key: 'options', enabled: true },
    { label: 'Créditos', key: 'credits', enabled: true },
  ]

  private optionTexts: Phaser.GameObjects.Text[] = []

  private selectedIndex = 0

  private infoText?: Phaser.GameObjects.Text

  private titleText?: Phaser.GameObjects.Text

  private hintText?: Phaser.GameObjects.Text

  private modalContainer?: Phaser.GameObjects.Container

  private modalBackground?: Phaser.GameObjects.Rectangle

  private modalText?: Phaser.GameObjects.Text

  private modalCloseButton?: Phaser.GameObjects.Text

  private lastNavSound = 0

  constructor() {
    super('MenuScene')
  }

  create(): void {
    this.titleText = this.add
      .text(0, 0, 'GI Joe: Operación Cobra', {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        color: '#f7f7f7',
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)

    this.optionTexts = this.options.map((option, index) => {
      const text = this.add
        .text(0, 0, option.label, {
          fontFamily: 'Segoe UI, system-ui, sans-serif',
          color: '#d9d9d9',
          align: 'center',
        })
        .setOrigin(0.5, 0.5)

      if (option.enabled) {
        text.setInteractive({ useHandCursor: true })
        text.on('pointerover', () => this.setSelection(index))
        text.on('pointerdown', () => this.handleSelection(index))
      } else {
        text.setAlpha(0.55)
      }

      return text
    })

    this.hintText = this.add
      .text(0, 0, 'Usa flechas o ratón. ENTER para seleccionar.', {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        color: '#9ca3af',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)

    this.input.keyboard?.on('keydown-UP', this.handleUp, this)
    this.input.keyboard?.on('keydown-DOWN', this.handleDown, this)
    this.input.keyboard?.on('keydown-ENTER', this.handleEnter, this)
    this.input.keyboard?.on('keydown-SPACE', this.handleEnter, this)

    this.scale.on('resize', this.handleResize, this)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.handleResize, this)
      this.input.keyboard?.off('keydown-UP', this.handleUp, this)
      this.input.keyboard?.off('keydown-DOWN', this.handleDown, this)
      this.input.keyboard?.off('keydown-ENTER', this.handleEnter, this)
      this.input.keyboard?.off('keydown-SPACE', this.handleEnter, this)
      this.input.gamepad?.off(Phaser.Input.Gamepad.Events.CONNECTED, this.handleGamepadConnected, this)
      this.input.gamepad?.off(Phaser.Input.Gamepad.Events.DISCONNECTED, this.handleGamepadDisconnected, this)

      const activePad = this.input.gamepad?.pad1
      if (activePad) {
        activePad.off(Phaser.Input.Gamepad.Events.BUTTON_DOWN, this.handleGamepadButton, this)
        activePad.off('axismove', this.handleGamepadAxis, this)
      }
    })

    this.handleResize()

    const enabledIndexes = this.getEnabledIndexes()
    this.selectedIndex = enabledIndexes[0] ?? 0
    this.refreshSelection()

    this.input.gamepad?.on(Phaser.Input.Gamepad.Events.CONNECTED, this.handleGamepadConnected, this)
    this.input.gamepad?.on(Phaser.Input.Gamepad.Events.DISCONNECTED, this.handleGamepadDisconnected, this)

    if (this.input.gamepad?.total) {
      const pad = this.input.gamepad.pad1
      if (pad) {
        this.registerGamepad(pad)
      }
    }

    this.ensureIntroMusic()
  }

  private setSelection(index: number): void {
    if (!this.options[index].enabled) {
      return
    }

    this.selectedIndex = index
    this.refreshSelection()
  }

  private handleSelection(index: number): void {
    if (!this.options[index].enabled) {
      this.showInfo('Disponible en próximas versiones')
      return
    }

    const option = this.options[index]

    if (option.key === 'play') {
      this.playNavigationSound(0.3)
      this.stopMenuMusic()
      this.scene.start('IntroScene')
      return
    }

    if (option.key === 'continue') {
      this.playNavigationSound(0.3)
      this.showInfo('No hay partida guardada')
      return
    }

    this.playNavigationSound(0.3)

    if (option.key === 'options') {
      this.showModal(
        'Opciones',
        'Configura idioma, audio y subtítulos.\n\nEstas opciones estarán disponibles cuando el sistema de guardado esté integrado.'
      )
      return
    }

    if (option.key === 'credits') {
      this.showModal(
        'Créditos',
        'Demo creada por el equipo de GI Joe: Operación Cobra.\n\nArte, música y efectos de sonido en preparación.'
      )
      return
    }
  }


  private refreshSelection(): void {
    this.optionTexts.forEach((text, index) => {
      const option = this.options[index]
      const isSelected = index === this.selectedIndex
      const baseColor = option.enabled ? '#d9d9d9' : '#8b8f99'

      text.setColor(isSelected ? '#f7b538' : baseColor)
      text.setFontStyle(isSelected ? 'bold' : 'normal')
      text.setAlpha(option.enabled ? 1 : 0.55)
    })
  }

  private handleUp(): void {
    const enabledIndexes = this.getEnabledIndexes()

    if (enabledIndexes.length === 0) {
      return
    }

    const currentPosition = enabledIndexes.indexOf(this.selectedIndex)
    const nextPosition = (currentPosition - 1 + enabledIndexes.length) % enabledIndexes.length

    this.selectedIndex = enabledIndexes[nextPosition]
    this.refreshSelection()
  }

  private handleDown(): void {
    const enabledIndexes = this.getEnabledIndexes()

    if (enabledIndexes.length === 0) {
      return
    }

    const currentPosition = enabledIndexes.indexOf(this.selectedIndex)
    const nextPosition = (currentPosition + 1) % enabledIndexes.length

    this.selectedIndex = enabledIndexes[nextPosition]
    this.refreshSelection()
  }

  private handleEnter(): void {
    this.handleSelection(this.selectedIndex)
  }

  private getEnabledIndexes(): number[] {
    return this.options.reduce<number[]>((acc, option, index) => {
      if (option.enabled) {
        acc.push(index)
      }

      return acc
    }, [])
  }

  private showInfo(message: string): void {
    this.infoText?.destroy()

    this.infoText = this.add
      .text(this.scale.width / 2, this.scale.height - 60, message, {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        color: '#e5e7eb',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)

    this.tweens.add({
      targets: this.infoText,
      alpha: 0,
      duration: 600,
      ease: 'Sine.easeOut',
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        this.infoText?.destroy()
        this.infoText = undefined
      },
    })
  }

  private handleResize(gameSize?: Phaser.Structs.Size): void {
    const width = gameSize?.width ?? this.scale.width
    const height = gameSize?.height ?? this.scale.height
    const minDimension = Math.min(width, height)

    const titleFontSize = Math.max(36, Math.round(minDimension * 0.07))
    const optionFontSize = Math.max(24, Math.round(minDimension * 0.045))
    const hintFontSize = Math.max(16, Math.round(minDimension * 0.03))
    const optionSpacing = optionFontSize * 1.6

    this.titleText?.setFontSize(titleFontSize)
    this.titleText?.setPosition(width / 2, Math.max(titleFontSize * 0.75, height * 0.22))

    const totalOptionsHeight = optionSpacing * (this.options.length - 1)
    const startY = height / 2 - totalOptionsHeight / 2

    this.optionTexts.forEach((text, index) => {
      text.setFontSize(optionFontSize)
      text.setPosition(width / 2, startY + index * optionSpacing)
    })

    const hintY = Math.min(height - hintFontSize * 2, startY + this.options.length * optionSpacing + optionSpacing * 0.6)

    this.hintText?.setFontSize(hintFontSize)
    this.hintText?.setPosition(width / 2, hintY)
    this.hintText?.setWordWrapWidth(Math.min(width * 0.9, 820), true)

    if (this.infoText) {
      this.infoText.setFontSize(hintFontSize)
      this.infoText.setPosition(width / 2, Math.min(height - hintFontSize * 2, hintY + optionSpacing * 0.8))
      this.infoText.setWordWrapWidth(Math.min(width * 0.9, 820), true)
    }

    if (this.modalContainer) {
      const modalWidth = Math.min(width * 0.8, 620)
      const modalHeight = Math.min(height * 0.7, 420)

      this.modalBackground?.setSize(modalWidth, modalHeight)
      this.modalContainer.setPosition(width / 2, height / 2)
      this.modalText?.setWordWrapWidth(modalWidth - 64, true)
      this.modalText?.setPosition(0, -modalHeight * 0.16)
      this.modalCloseButton?.setPosition(0, modalHeight / 2 - 48)
    }
  }

  private showModal(title: string, body: string): void {
    this.destroyModal()

    const modalBackground = this.add
      .rectangle(0, 0, 100, 100, 0x0f172a, 0.92)
      .setStrokeStyle(2, 0xf7b538, 0.6)
      .setOrigin(0.5, 0.5)

    const modalTitle = this.add.text(0, 0, title, {
      fontFamily: 'Segoe UI, system-ui, sans-serif',
      fontSize: '30px',
      color: '#f9fafb',
      fontStyle: 'bold',
      align: 'center',
    })

    const modalBody = this.add.text(0, 0, body, {
      fontFamily: 'Segoe UI, system-ui, sans-serif',
      fontSize: '20px',
      color: '#e5e7eb',
      align: 'center',
      wordWrap: { width: 500 },
      lineSpacing: 8,
    })

    modalTitle.setOrigin(0.5, 0.5)
    modalBody.setOrigin(0.5, 0.5)
    modalBody.setPosition(0, 40)

    const closeButton = this.add
      .text(0, 0, 'Cerrar', {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '22px',
        color: '#f7b538',
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })

    closeButton.on('pointerdown', () => {
      this.playNavigationSound(0.3)
      this.destroyModal()
    })

    this.modalContainer = this.add.container(0, 0, [modalBackground, modalTitle, modalBody, closeButton])
    this.modalContainer.setDepth(Number.MAX_SAFE_INTEGER - 1)
    this.modalContainer.setAlpha(0)

    this.modalBackground = modalBackground
    this.modalText = modalBody
    this.modalCloseButton = closeButton

    this.tweens.add({
      targets: this.modalContainer,
      alpha: 1,
      duration: 200,
      ease: 'Sine.easeOut',
    })

    this.handleResize()
  }

  private destroyModal(): void {
    if (!this.modalContainer) {
      return
    }

    this.modalContainer.destroy(true)
    this.modalContainer = undefined
    this.modalBackground = undefined
    this.modalText = undefined
    this.modalCloseButton = undefined
  }

  private handleGamepadConnected(pad: Phaser.Input.Gamepad.Gamepad): void {
    this.registerGamepad(pad)
  }

  private handleGamepadDisconnected(pad: Phaser.Input.Gamepad.Gamepad): void {
    pad.off(Phaser.Input.Gamepad.Events.BUTTON_DOWN, this.handleGamepadButton, this)
    pad.off('axismove', this.handleGamepadAxis, this)
  }

  private registerGamepad(pad: Phaser.Input.Gamepad.Gamepad): void {
    pad.on(Phaser.Input.Gamepad.Events.BUTTON_DOWN, this.handleGamepadButton, this)
    pad.on('axismove', this.handleGamepadAxis, this)
  }

  private handleGamepadButton(_: Phaser.Input.Gamepad.Gamepad, button: Phaser.Input.Gamepad.Button): void {
    if (button.index === Phaser.Input.Gamepad.Configs.XBOX_360.A) {
      this.handleEnter()
    }

    if (button.index === Phaser.Input.Gamepad.Configs.XBOX_360.B) {
      if (this.modalContainer) {
        this.playNavigationSound(0.3)
        this.destroyModal()
      }
    }

    if (button.index === Phaser.Input.Gamepad.Configs.XBOX_360.UP) {
      this.handleUp()
      this.playNavigationSound()
    }

    if (button.index === Phaser.Input.Gamepad.Configs.XBOX_360.DOWN) {
      this.handleDown()
      this.playNavigationSound()
    }
  }

  private handleGamepadAxis(_: Phaser.Input.Gamepad.Gamepad, axisIndex: number, value: number): void {
    const now = this.time.now
    if (Math.abs(value) < 0.4) {
      return
    }

    if (now - this.lastNavSound < 220) {
      return
    }

    if (axisIndex === 1) {
      if (value < 0) {
        this.handleUp()
      } else {
        this.handleDown()
      }

      this.playNavigationSound()
      this.lastNavSound = now
    }
  }

  private playNavigationSound(volume = 0.3): void {
    if (!this.sound || !this.cache.audio.exists('ui-click')) {
      return
    }

    this.sound.play('ui-click', { volume })
  }

  private ensureIntroMusic(): void {
    if (!this.sound) {
      return
    }

    this.sound.get('campaign-theme')?.stop()

    if (!this.cache.audio.exists('intro-theme')) {
      return
    }

    const existing = this.sound.get('intro-theme')

    if (existing) {
      if (!existing.isPlaying) {
        existing.play({ loop: true, volume: 0.3 })
      }
      return
    }

    this.sound.add('intro-theme', { loop: true, volume: 0.3 }).play()
  }

  private stopMenuMusic(): void {
    if (!this.sound) {
      return
    }

    this.sound.get('intro-theme')?.stop()
    this.sound.get('campaign-theme')?.stop()
  }
}
